import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import carDetails from "../data/carDetails.json";
import {
  fetchOwnerProfile,
  fetchOwnerVehiclesWithBookings,
  deleteOwnerVehicle,
} from "../api/ownerCarsApi";
import { toast } from "../lib/toast";
import VehicleImage from "../components/common/VehicleImage";
import {
  buildVehicleImageIndex,
  getVehicleImageFromIndex,
} from "../utils/vehicleImage";

function safe(value) {
  return value === null || value === undefined || value === "" ? "—" : value;
}

function DetailItem({ label, value }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
      <div className="text-[10px] font-medium uppercase tracking-[0.14em] text-slate-500">
        {label}
      </div>
      <div className="mt-1 text-[13px] font-semibold leading-snug text-slate-900">
        {safe(value)}
      </div>
    </div>
  );
}

function getComplianceMeta(vehicle) {
  const verified = vehicle?.complianceVerified === true;
  const complete = vehicle?.complianceComplete === true;

  if (verified) {
    return {
      key: "VERIFIED",
      label: "Verified",
      hint: null,
      chipClass: "border-emerald-200 bg-emerald-50 text-emerald-800",
    };
  }

  if (complete && !verified) {
    return {
      key: "UNDER_REVIEW",
      label: "Under review",
      hint: "Documents submitted. Verification is in progress.",
      chipClass: "border-amber-200 bg-amber-50 text-amber-900",
    };
  }

  return {
    key: "DOCUMENTS_REQUIRED",
    label: "Documents required",
    hint: "Upload photos and documents to enable bookings.",
    chipClass: "border-rose-200 bg-rose-50 text-rose-700",
  };
}

function ComplianceBadge({ vehicle }) {
  const meta = getComplianceMeta(vehicle);

  return (
    <span
      title={meta.hint || meta.label}
      className={`inline-flex items-center rounded-md border px-2.5 py-1 text-[11px] font-semibold ${meta.chipClass}`}
    >
      {meta.label}
    </span>
  );
}

function isNonCarVehicle(vehicle) {
  const value = String(vehicle?.type || vehicle?.brand || vehicle?.model || "").toLowerCase();
  return value.includes("auto") || value.includes("rickshaw") || value.includes("rikshaw") || value.includes("erickshaw") || value.includes("van") || value.includes("traveller");
}

function VehicleCard({ vehicle, imageUrl, onSeeBookings, onEdit, onDocs, onRemove, removing }) {
  const {
    brand,
    model,
    registrationNumber,
    fuelType,
    color,
    yearOfPurchase,
    vehicleLocation,
    seatingCapacity,
    openForBookingStates,
    ratingAverage,
    ratingCount,
  } = vehicle;

  const nonCarVehicle = isNonCarVehicle(vehicle);
  const complianceMeta = getComplianceMeta(vehicle);
  const bookingStatesLabel = openForBookingStates
    ? String(openForBookingStates)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .join(", ")
    : "Not set";

  const seatsValue = Number.isFinite(Number(seatingCapacity))
    ? Number(seatingCapacity)
    : seatingCapacity;

  const avgNum = ratingAverage !== null && ratingAverage !== undefined ? Number(ratingAverage) : null;
  const countNum = ratingCount !== null && ratingCount !== undefined ? Number(ratingCount) : 0;
  const hasRating = avgNum !== null && Number.isFinite(avgNum) && countNum > 0;

  return (
    <article className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:shadow-md">
      <div className="flex flex-col lg:grid lg:grid-cols-[300px_minmax(0,1fr)_220px] xl:grid-cols-[320px_minmax(0,1fr)_240px]">
        <div className="w-full border-b border-slate-200 bg-white lg:border-b-0 lg:border-r">
          <div className="flex h-[180px] items-center justify-center p-4 sm:h-[200px] sm:p-5 lg:h-full lg:min-h-[188px] lg:p-5">
            <VehicleImage
              imageUrl={imageUrl}
              brand={brand}
              model={model}
              color={color}
              type={vehicle?.type}
              alt={`${brand || ""} ${model || ""} ${color || ""}`.trim() || "Vehicle image"}
              wrapperClassName="block h-full w-full"
              className="block h-full w-full object-contain object-center"
            />
          </div>
        </div>

        <div className="flex-1 p-4 md:p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="min-w-0 text-[18px] font-semibold leading-tight tracking-tight text-slate-900 md:text-[20px]">
                  {nonCarVehicle ? safe(vehicle?.type || brand || model) : <>{safe(brand)} <span className="text-slate-800">{safe(model)}</span></>}
                </h2>
                <ComplianceBadge vehicle={vehicle} />
              </div>

              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] text-slate-700">
                  <span className="text-[10px] uppercase tracking-[0.12em] text-slate-500">Reg</span>
                  <span className="h-1 w-1 rounded-full bg-slate-300" />
                  <span className="tabular-nums">{safe(registrationNumber)}</span>
                </span>

                {hasRating && (
                  <span className="inline-flex items-center gap-1.5 rounded-md border border-[#FF2D83]/15 bg-[#FF2D83]/5 px-2.5 py-1 text-[11px] font-semibold text-slate-900">
                    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-3.5 w-3.5 text-[#FF2D83]">
                      <path d="M12 2.5l2.59 5.26 5.81.84-4.2 4.09.99 5.77L12 15.9l-5.19 2.73.99-5.77-4.2-4.09 5.81-.84L12 2.5z" fill="currentColor" />
                    </svg>
                    <span>{avgNum.toFixed(1)}</span>
                    <span className="text-slate-500">· {countNum}</span>
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            <DetailItem label={nonCarVehicle ? "Base location" : "Car location"} value={vehicleLocation || "Not set"} />
            <DetailItem label={nonCarVehicle ? "Vehicle service" : "Booking locations"} value={nonCarVehicle ? (vehicle?.type || brand || "—") : bookingStatesLabel} />
            <DetailItem label="Year of purchase" value={yearOfPurchase || "—"} />
            <DetailItem label={nonCarVehicle ? "Model" : "Color"} value={nonCarVehicle ? (model || "—") : (color || "—")} />
            <DetailItem label={nonCarVehicle ? "Brand" : "Fuel"} value={nonCarVehicle ? (brand || "—") : (fuelType || "—")} />
            <DetailItem label="Seats" value={seatsValue || "—"} />
          </div>

          {complianceMeta.key !== "VERIFIED" && complianceMeta.hint && (
            <div className="mt-3 inline-flex items-center rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-700">
              {complianceMeta.hint}
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 p-4 lg:border-l lg:border-t-0 lg:p-4">
          <div className="flex h-full flex-col justify-between gap-3">
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
              <div className="px-4 pb-3 pt-3">
                <div className="text-[11px] uppercase tracking-wide text-slate-500">Fleet status</div>
                <div className="mt-1 text-sm font-semibold text-slate-900">{complianceMeta.label}</div>
              </div>
              <div className="border-t border-slate-100 px-4 py-2 text-[11px] text-slate-500">
                Keep basics, photos and documents updated for smooth bookings.
              </div>
            </div>

            <button
              type="button"
              onClick={onSeeBookings}
              className="inline-flex h-10 w-full items-center justify-center rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-800 transition hover:border-slate-900 hover:bg-slate-900 hover:text-white"
            >
              See bookings
            </button>

            <button
              type="button"
              onClick={onEdit}
              className="inline-flex h-10 w-full items-center justify-center rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-800 transition hover:border-slate-900 hover:bg-slate-900 hover:text-white"
            >
              Edit details
            </button>

            <button
              type="button"
              onClick={onDocs}
              className={`inline-flex h-10 w-full items-center justify-center rounded-lg border px-4 text-sm font-semibold transition ${
                complianceMeta.key === "UNDER_REVIEW"
                  ? "border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-100"
                  : complianceMeta.key === "DOCUMENTS_REQUIRED"
                  ? "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"
                  : "border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
              }`}
            >
              Documents &amp; photos
            </button>

            <button
              type="button"
              onClick={onRemove}
              disabled={removing}
              className="inline-flex h-10 w-full items-center justify-center rounded-lg border border-red-200 bg-white px-4 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {removing ? "Removing…" : "Remove vehicle"}
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function ManageCars() {
  const [ownerDetails, setOwnerDetails] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [vehicleToDelete, setVehicleToDelete] = useState(null);
  const [removingReg, setRemovingReg] = useState(null);

  const navigate = useNavigate();
  const imageIndex = useMemo(() => buildVehicleImageIndex(carDetails), []);

  useEffect(() => {
    (async () => {
      try {
        const owner = await fetchOwnerProfile();
        setOwnerDetails(owner);

        if (!owner?.id) throw new Error("Owner profile missing id");

        const list = await fetchOwnerVehiclesWithBookings(owner.id);
        setVehicles(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error("Error loading owner/vehicles:", err);
        setError("We couldn't load your cars right now. Please try again.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const totalVehicles = vehicles.length;

  const sortedVehicles = useMemo(() => {
    const rank = (v) => {
      const m = getComplianceMeta(v);
      if (m.key === "DOCUMENTS_REQUIRED") return 0;
      if (m.key === "UNDER_REVIEW") return 1;
      return 2;
    };
    return [...vehicles].sort((a, b) => rank(a) - rank(b));
  }, [vehicles]);

  const getVehicleImage = (vehicle) => {
    const catalogImage =
      getVehicleImageFromIndex(imageIndex, vehicle?.brand, vehicle?.model, vehicle?.color) || null;
    if (catalogImage) return catalogImage;

    const uploadedImage = Array.isArray(vehicle?.photos) ? vehicle.photos.find(Boolean) : null;
    return uploadedImage || null;
  };

  const handleBack = () => navigate("/owner/dashboard");
  const handleAddVehicle = () => navigate("/owner/cars/new");
  const handleSeeBookings = (vehicle) => {
    const reg = vehicle?.registrationNumber;
    navigate(reg ? `/owner/bookings?car=${encodeURIComponent(reg)}` : "/owner/bookings");
  };
  const handleEditVehicle = (vehicle) => {
    const editPath = isNonCarVehicle(vehicle)
      ? `/owner/cars/${encodeURIComponent(vehicle.registrationNumber)}/edit/auto-erikshaw`
      : `/owner/cars/${encodeURIComponent(vehicle.registrationNumber)}/edit`;
    navigate(editPath, {
      state: { existingVehicle: vehicle },
    });
  };
  const handleDocsClick = (vehicle) => {
    const reg = vehicle?.registrationNumber;
    if (!reg) {
      toast.info("Please complete vehicle basics first.");
      return;
    }
    navigate(`/owner/cars/${encodeURIComponent(reg)}/documents`, { state: { vehicle } });
  };
  const handleRemoveClick = (vehicle) => setVehicleToDelete(vehicle);

  const handleConfirmRemove = async () => {
    if (!vehicleToDelete?.registrationNumber) return;
    const { registrationNumber } = vehicleToDelete;

    try {
      setRemovingReg(registrationNumber);
      await deleteOwnerVehicle(registrationNumber);
      setVehicles((prev) => prev.filter((v) => v.registrationNumber !== registrationNumber));
      toast.success("Vehicle removed from your fleet.");
      setVehicleToDelete(null);
    } catch (err) {
      console.error("Failed to remove vehicle:", err);
      toast.error(err?.response?.data || "We couldn't remove this vehicle. Please try again.");
    } finally {
      setRemovingReg(null);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 text-slate-900">
      <Header />

      <main className="flex-1">
        <div className="mx-auto max-w-[1560px] px-4 py-5 sm:px-6 sm:py-6 lg:px-10 2xl:px-12">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Fleet console</p>
              <div className="mt-1 flex items-center gap-3">
                <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">Manage vehicles</h1>
                {totalVehicles > 0 && (
                  <div className="inline-flex items-center gap-2 rounded-lg border border-[#FF2D83] bg-white px-3 py-1.5 text-[11px] font-medium text-slate-900">
                    <span className="grid h-6 min-w-[2rem] place-items-center rounded-md bg-[#FF2D83]/5 text-[11px] font-semibold text-[#FF2D83] tabular-nums">
                      {totalVehicles}
                    </span>
                    <span className="text-[11px] text-slate-700">vehicles listed on Wayroot</span>
                  </div>
                )}
              </div>
              {ownerDetails && (
                <p className="mt-2 text-[11px] font-medium text-slate-500">
                  Managing vehicles under <span className="font-semibold text-slate-800">{ownerDetails.name || ownerDetails.email}</span>.
                </p>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={handleBack}
                className="inline-flex h-10 items-center justify-center rounded-xl border border-slate-300 bg-white px-4 text-xs font-semibold text-slate-700 shadow-sm transition-colors hover:border-slate-900 hover:bg-slate-900 hover:text-white sm:text-sm"
              >
                Back to dashboard
              </button>
              <button
                type="button"
                onClick={handleAddVehicle}
                className="inline-flex h-10 items-center justify-center rounded-xl bg-slate-900 px-5 text-xs font-semibold text-white shadow-[0_10px_28px_rgba(15,23,42,0.45)] transition-transform transition-shadow hover:translate-y-px hover:shadow-[0_8px_20px_rgba(15,23,42,0.45)] active:translate-y-[2px] active:shadow-[0_4px_12px_rgba(15,23,42,0.5)] sm:text-sm"
              >
                Add vehicle
              </button>
            </div>
          </div>

          {loading && <div className="mt-10 grid place-items-center text-sm text-slate-500">Loading your vehicles…</div>}

          {!loading && error && (
            <div className="mt-8 rounded-3xl border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-700">{error}</div>
          )}

          {!loading && !error && totalVehicles === 0 && (
            <div className="mt-8 rounded-3xl border border-dashed border-slate-300 bg-white/80 px-6 py-8 text-center">
              <h2 className="text-sm font-semibold text-slate-900">No vehicles added yet</h2>
              <p className="mt-2 text-xs text-slate-600">Add your first car, Auto, E Rikshaw, or Van to start receiving trip requests.</p>
              <button
                type="button"
                onClick={handleAddVehicle}
                className="mt-4 inline-flex h-10 items-center justify-center rounded-xl bg-slate-900 px-5 text-xs font-semibold text-white shadow-[0_12px_30px_rgba(15,23,42,0.45)] hover:translate-y-px hover:shadow-[0_10px_24px_rgba(15,23,42,0.45)]"
              >
                List a vehicle now
              </button>
            </div>
          )}

          {!loading && !error && totalVehicles > 0 && (
            <div className="mt-6 space-y-4 md:space-y-5">
              {sortedVehicles.map((vehicle, index) => (
                <VehicleCard
                  key={vehicle?.registrationNumber || vehicle?.id || index}
                  vehicle={vehicle}
                  imageUrl={getVehicleImage(vehicle)}
                  onSeeBookings={() => handleSeeBookings(vehicle)}
                  onEdit={() => handleEditVehicle(vehicle)}
                  onDocs={() => handleDocsClick(vehicle)}
                  onRemove={() => handleRemoveClick(vehicle)}
                  removing={removingReg === vehicle?.registrationNumber}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />

      {vehicleToDelete && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40">
          <div className="mx-4 w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-xl sm:p-6">
            <h2 className="text-sm font-semibold text-slate-900">Remove vehicle from your fleet?</h2>
            <p className="mt-2 text-xs text-slate-600">
              This vehicle will no longer appear in your Wayroot listings. You can add it again later if needed.
            </p>

            <div className="mt-3 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-700">
              <div className="font-semibold text-slate-900">
                {(vehicleToDelete.brand || "") + (vehicleToDelete.model ? ` ${vehicleToDelete.model}` : "")}
              </div>
              {vehicleToDelete.registrationNumber && (
                <div className="mt-0.5 text-[11px] text-slate-500">
                  Reg: <span className="tabular-nums">{vehicleToDelete.registrationNumber}</span>
                </div>
              )}
            </div>

            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => !removingReg && setVehicleToDelete(null)}
                disabled={!!removingReg}
                className="inline-flex h-9 items-center justify-center rounded-lg border border-slate-300 bg-white px-4 text-xs font-semibold text-slate-700 hover:border-slate-900 hover:bg-slate-900 hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmRemove}
                disabled={!!removingReg}
                className="inline-flex h-9 items-center justify-center rounded-lg border border-red-600 bg-red-600 px-4 text-xs font-semibold text-white shadow-sm hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {removingReg ? "Removing…" : "Yes, remove vehicle"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
